/* eslint-disable react/prop-types */
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Box, CircularProgress, Fade, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useFilterState } from '../../../stores/FilterStateStore';
import { getUrlParamFromEncodedState } from '../../../utilities/FilterStateUrlUtilities';
import useDebounced from '../../../utilities/UseDebounced';
import { businessServiceFilterId, timeRangeFilterId } from '../../filters';
import StatusBlock from '../../status-block/StatusBlock';
import { hasBusinessServiceGroupings, useGroupedProblemCount, useGroupedSiteProblems } from './UseSiteData';
import { DrillInLink } from '../../links/DrillInLink';

const useProblemsListStyles = makeStyles((theme) => ({
    root: {
        cursor: 'default',
    },

    problemTitle: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,

        '&>*': {
            marginRight: theme.spacing(1),
        },
    },
}));

export default function ProblemsList(props) {
    const { className, timeRange, site, bsGroup } = props;
    const [showLoader, setShowLoader] = useState(false);
    const setShowLoadingDebounced = useDebounced(setShowLoader, 200);
    const classes = useProblemsListStyles();
    const [problemData, isLoading, error] = useGroupedSiteProblems({ timeRange, site, bsGroup });

    // we don't want to wipe out problem data between reloads (timerange autorefresh), as it makes
    // everthing jump around. Keep the last data until our new data loads
    const lastProblemData = useRef();
    if (!isLoading) {
        lastProblemData.current = problemData;
    }

    // don't show the loading indicator immediately (looks weird when flashing for quick calls). Only
    // show it a bit after we've been waiting for data
    useEffect(() => {
        if (isLoading) {
            setShowLoadingDebounced(true);
        } else {
            setShowLoadingDebounced.cancel();
            setShowLoader(false);
        }
    }, [isLoading, setShowLoadingDebounced]);

    const { hasProblems, total, groupedProblems } = lastProblemData.current;
    const visibleProblems = useGroupedProblemCount(groupedProblems);

    return (
        <div className={clsx(classes.root, className)}>
            <div className={classes.problemTitle}>
                <Typography component="div" variant="subtitle2">
                    {hasProblems ? (
                        <Trans>
                            Problems ({visibleProblems} of {total})
                        </Trans>
                    ) : (
                        <Trans>Problems</Trans>
                    )}
                </Typography>

                {showLoader && (
                    <Fade in={showLoader}>
                        <CircularProgress size={16} />
                    </Fade>
                )}
            </div>

            {(!!error || !hasProblems) && (
                <Typography variant="caption">
                    {error && <Trans>An error occurred loading site problems!</Trans>}
                    {!hasProblems && !isLoading && <Trans>No problems found for this site</Trans>}
                </Typography>
            )}

            {hasProblems && <GroupedProblemList problemData={lastProblemData.current} />}
        </div>
    );
}

const useGroupedProblemListStyles = makeStyles((theme) => ({
    groupedProblem: {
        paddingLeft: theme.spacing(1),
    },

    groupHeader: {
        fontSize: theme.typography.caption.fontSize,
        paddingTop: theme.spacing(0.5),
    },
}));

function GroupedProblemList(props) {
    const { problemData } = props;
    const { groupedProblems } = problemData;
    const classes = useGroupedProblemListStyles();

    const isGrouped = hasBusinessServiceGroupings(groupedProblems);

    if (!isGrouped) {
        const [{ problems }] = groupedProblems;
        return problems.map((problem) => <Problem key={problem.problemId} problem={problem} />);
    }

    return groupedProblems.map((item) => (
        <div key={item.id}>
            <GroupedProblemHeader className={classes.groupHeader} item={item} />

            {item.problems.map((problem) => (
                <Problem key={problem.problemId} className={classes.groupedProblem} problem={problem} />
            ))}
        </div>
    ));
}

const useGroupedProblemHeaderStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,
    },
}));

function GroupedProblemHeader(props) {
    const { item, className } = props;
    const { id, name, problems, totalProblems } = item;
    const classes = useGroupedProblemHeaderStyles();

    const [filterState] = useFilterState();
    const currentTimeRange = filterState.encoded?.[timeRangeFilterId];

    const linkParams = id
        ? getUrlParamFromEncodedState({
              [businessServiceFilterId]: id,
              [timeRangeFilterId]: currentTimeRange,
          })
        : '';
    const url = `/business-service-detail?${linkParams}`;

    if (id) {
        return (
            <div className={clsx(classes.root, className)}>
                <DrillInLink to={url} title={i18n._(t`Click to view details for Business Service: ${name}`)}>
                    {name}
                </DrillInLink>
                <Box ml={0.5}>
                    ({problems.length} of {totalProblems})
                </Box>
            </div>
        );
    }

    return (
        <div className={className}>
            <Trans>
                Other ({problems.length} of {totalProblems})
            </Trans>
        </div>
    );
}

const useProblemStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,

        minWidth: 0,
        overflow: 'hidden',
        cursor: 'default',
    },

    clipOverflow: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },

    status: {
        width: 10,
        minWidth: 10,
        marginRight: theme.spacing(1),
    },

    link: {
        fontSize: theme.typography.caption.fontSize,
    },
}));

function Problem(props) {
    const classes = useProblemStyles();

    const { className, problem } = props;
    const {
        entity: { name, page, type, uuid },
    } = problem;

    const [filterState] = useFilterState();
    const currentTimeRange = filterState.encoded?.[timeRangeFilterId];

    const params = getUrlParamFromEncodedState({
        [type]: uuid,
        [timeRangeFilterId]: currentTimeRange,
    });
    const problemUrl = `/${page}?${params}`;

    const problemTooltip = `[${problem.severity.worst}] ${name} - ${problem.description}`;

    return (
        <div className={clsx(className, classes.root)} title={problemTooltip}>
            <div className={classes.status}>
                <StatusBlock size={10} color={convertStatusColor(problem.severity.worst)} />
            </div>

            <DrillInLink to={problemUrl} className={classes.link}>
                {name}
            </DrillInLink>

            <Typography variant="caption">&nbsp;-&nbsp;</Typography>

            <Typography variant="caption" className={classes.clipOverflow}>
                {problem.description}
            </Typography>
        </div>
    );
}

function convertStatusColor(worstStatus) {
    return (
        {
            Normal: 'green',
            Degraded: 'yellow',
            Excessive: 'orange',
            Critical: 'red',
        }[worstStatus] || 'gray'
    );
}
